/**
 * usePoseMedia - Hook to fetch all pose video overrides for a kind.
 * Returns a map { pose_id: { url, locked, ... } } and helpers.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_VIDEO_BYTES = 500 * 1024 * 1024; // 500 MB

export default function usePoseMedia(kind /* "yoga" | "stretch" */) {
  const [media, setMedia] = useState({}); // pose_id -> doc
  const [loading, setLoading] = useState(true);
  // Track uploads per poseId: { [poseId]: { progress: 0-100, name } }
  const [uploads, setUploads] = useState({});

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/pose-media/${kind}`);
      const map = {};
      (r.data || []).forEach(d => {
        // Prefer the resilient /api/pose-media/file/{id} URL (DB-backed, deploy-safe).
        // Fall back to the legacy disk URL.
        const playUrl = d.video_url || d.url;
        map[d.pose_id] = { ...d, url: playUrl };
      });
      setMedia(map);
    } catch (e) {
      console.warn('[usePoseMedia] refresh failed:', e);
      setMedia({});
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => { refresh(); }, [refresh]);

  const upload = async (poseId, file) => {
    if (!file) return;
    if (file.size > MAX_VIDEO_BYTES) {
      toast.error(`Vidéo trop volumineuse (${(file.size / 1024 / 1024).toFixed(0)} Mo). Maximum 500 Mo. Compressez-la avant de réessayer.`);
      return;
    }
    setUploads((u) => ({ ...u, [poseId]: { progress: 0, name: file.name, size: file.size } }));
    const sizeMb = (file.size / 1024 / 1024).toFixed(1);
    const toastId = toast.loading(`Envoi de la vidéo (${sizeMb} Mo)…`);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API}/api/pose-media/${kind}/${encodeURIComponent(poseId)}/upload`,
        fd,
        {
          // No global timeout — large videos on mobile networks can take a while.
          timeout: 0,
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setUploads((u) => ({ ...u, [poseId]: { ...(u[poseId] || {}), progress: pct } }));
            toast.loading(`Envoi (${sizeMb} Mo) — ${pct}%`, { id: toastId });
          },
        }
      );
      toast.success('Vidéo téléversée ✓', { id: toastId });
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail
        || (e?.code === 'ERR_NETWORK' ? 'Connexion interrompue. Réessayez sur Wi-Fi.' : null)
        || (e?.message?.includes('timeout') ? 'Délai dépassé. Connexion trop lente.' : null)
        || 'Échec de l\'upload. Vérifiez votre connexion.';
      toast.error(detail, { id: toastId });
      console.error('[usePoseMedia] upload error:', e);
    } finally {
      setUploads((u) => { const n = { ...u }; delete n[poseId]; return n; });
    }
  };

  const remove = async (poseId) => {
    if (!window.confirm('Supprimer la vidéo de cette posture ?')) return;
    try {
      await axios.delete(`${API}/api/pose-media/${kind}/${encodeURIComponent(poseId)}`);
      toast.success('Vidéo supprimée');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suppression impossible');
    }
  };

  const toggleLock = async (poseId) => {
    try {
      const r = await axios.put(`${API}/api/pose-media/${kind}/${encodeURIComponent(poseId)}/lock`);
      toast.success(r.data.locked ? 'Vidéo verrouillée 🔒' : 'Vidéo déverrouillée');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action impossible');
    }
  };

  return { media, loading, uploads, upload, remove, toggleLock, refresh };
}
